// Helper pour gérer les champs personnalisés

/**
 * Prépare un serveur pour l'enregistrement en remplaçant les valeurs "custom" par leurs valeurs personnalisées
 * @param {Object} server - Objet serveur à préparer
 * @returns {Object} - Serveur préparé avec les valeurs personnalisées
 */
export const prepareServerForSave = (server) => {
  if (!server) return {};
  
  // Déboguer l'entrée de la fonction
  console.log('prepareServerForSave - Entrée:', server.name || 'serveur sans nom');
  
  // Faire une copie plus sûre de l'objet serveur pour ne pas modifier l'original
  // Utiliser le spread operator au lieu de JSON.parse(JSON.stringify()) pour éviter les problèmes avec les grandes chaînes
  const preparedServer = { ...server };
  
  // Liste des champs qui peuvent avoir des valeurs personnalisées
  const customizableFields = [
    { field: 'os', customField: 'customOs' },
    { field: 'ram', customField: 'customRam' },
    { field: 'cpu', customField: 'customCpu' },
    { field: 'diskSpace', customField: 'customDiskSpace' },
    { field: 'mariadbVersion', customField: 'customMariadbVersion' },
    { field: 'apacheVersion', customField: 'customApacheVersion' },
    { field: 'nodejsVersion', customField: 'customNodejsVersion' }
  ];
  
  // Pour chaque champ personnalisable
  customizableFields.forEach(({ field, customField }) => {
    // Si la valeur du champ est "custom" et qu'il y a une valeur personnalisée
    if (preparedServer[field] === 'custom' && preparedServer[customField]) {
      // Remplacer la valeur "custom" par la valeur personnalisée
      console.log(`Remplacement: ${field}='custom' -> ${preparedServer[customField]}`);
      preparedServer[field] = preparedServer[customField];
    }
  });
  
  // Pour la sauvegarde finale, on crée un nouvel objet sans les champs custom
  const finalServer = {};
  
  // Copier tous les champs sauf les custom
  for (const key in preparedServer) {
    // Ne pas copier les champs custom mais copier tout le reste
    if (!key.startsWith('custom')) {
      finalServer[key] = preparedServer[key];
    }
  }
  
  console.log('prepareServerForSave - Sortie:', finalServer.name || 'serveur sans nom');
  return finalServer;
};

/**
 * Prépare un client pour l'enregistrement en remplaçant les valeurs "custom" par leurs valeurs personnalisées
 * @param {Object} client - Objet client à préparer
 * @returns {Object} - Client préparé avec les valeurs personnalisées
 */
export const prepareClientForSave = (client) => {
  if (!client) return {};
  
  // Faire une copie plus sûre du client qui préserve les données importantes
  const preparedClient = {
    ...client,
    // Préserver explicitement connectionProcedure sans le passer par JSON.stringify
    connectionProcedure: client.connectionProcedure,
    // Traiter les serveurs séparément
    servers: client.servers ? client.servers.map(server => prepareServerForSave(server)) : []
  };
  
  return preparedClient;
};

/**
 * Prépare un serveur pour l'affichage en détectant les valeurs personnalisées
 * @param {Object} server - Objet serveur à préparer
 * @param {Array} predefinedValues - Liste des valeurs prédéfinies pour chaque champ
 * @returns {Object} - Serveur préparé pour l'affichage
 */
export const prepareServerForDisplay = (server, predefinedValues) => {
  const preparedServer = { ...server };
  
  // Structure des champs personnalisables et leurs valeurs prédéfinies
  const customizableFields = [
    { field: 'os', customField: 'customOs', options: predefinedValues.osOptions },
    { field: 'ram', customField: 'customRam', options: predefinedValues.ramOptions },
    { field: 'cpu', customField: 'customCpu', options: predefinedValues.cpuOptions },
    { field: 'diskSpace', customField: 'customDiskSpace', options: predefinedValues.diskOptions },
    { field: 'mariadbVersion', customField: 'customMariadbVersion', options: predefinedValues.mariadbVersionOptions },
    { field: 'apacheVersion', customField: 'customApacheVersion', options: predefinedValues.apacheVersionOptions },
    { field: 'nodejsVersion', customField: 'customNodejsVersion', options: predefinedValues.nodejsVersionOptions }
  ];
  
  // Pour chaque champ personnalisable
  customizableFields.forEach(({ field, customField, options }) => {
    // Si la valeur n'est pas vide et n'est pas dans la liste des options prédéfinies
    if (preparedServer[field] && !options.includes(preparedServer[field])) {
      // Stocker la valeur dans le champ personnalisé
      preparedServer[customField] = preparedServer[field];
      // Définir le champ principal à "custom"
      preparedServer[field] = 'custom';
    }
  });
  
  return preparedServer;
};

/**
 * Prépare un client pour l'affichage en détectant les valeurs personnalisées
 * @param {Object} client - Objet client à préparer
 * @param {Object} predefinedValues - Objet contenant les valeurs prédéfinies
 * @returns {Object} - Client préparé pour l'affichage
 */
export const prepareClientForDisplay = (client, predefinedValues) => {
  const preparedClient = { ...client };
  
  // Traiter chaque serveur
  if (preparedClient.servers && preparedClient.servers.length > 0) {
    preparedClient.servers = preparedClient.servers.map(server => 
      prepareServerForDisplay(server, predefinedValues)
    );
  }
  
  return preparedClient;
};
